import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Minus, Plus, Trash2, Loader2, Tag, X, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, money } from "@/lib/api";

const FREE = 75;

const DiscountBox = ({ subtotal }) => {
  const { discount, setDiscount } = useCart();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const validate = async (c, silent) => {
    setBusy(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: c, subtotal });
      setDiscount(data); setCode("");
      if (!silent) toast.success(`Code ${data.code} applied`);
    } catch (e) {
      setDiscount(null);
      if (!silent) toast.error(formatApiError(e)); else toast(`Code ${c} no longer applies: ${formatApiError(e)}`);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (discount && subtotal > 0) validate(discount.code, true); }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (discount) return (
    <div data-testid="discount-applied" className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 px-3 h-10">
      <span className="flex items-center gap-2 font-mono text-xs text-emerald-400"><Tag className="w-3.5 h-3.5" />{discount.code} · {discount.type === "percent" ? `${discount.value}% off` : `${money(discount.value)} off`}</span>
      <button data-testid="discount-remove" onClick={() => setDiscount(null)} className="text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) validate(code.trim()); }} className="flex gap-2">
      <div className="flex-1 flex items-center bg-white/5 border border-white/15 focus-within:border-[#FF3B30] px-3 h-10">
        <Tag className="w-3.5 h-3.5 text-neutral-500" />
        <input data-testid="discount-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Discount code" className="bg-transparent outline-none text-sm font-mono px-2 w-full placeholder:text-neutral-500 placeholder:font-sans" />
      </div>
      <button data-testid="discount-apply-button" disabled={busy || !code.trim()} className="h-10 px-4 border border-white/20 hover:bg-white hover:text-black font-display uppercase font-bold tracking-wider text-sm disabled:opacity-40">{busy ? "..." : "Apply"}</button>
    </form>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, update, remove, subtotal, keyOf, discount } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [addressId, setAddressId] = useState("");
  const nav = useNavigate();
  const physicalSubtotal = items.filter((i) => !i.is_digital).reduce((s, i) => s + i.price * i.quantity, 0);
  const hasPhysical = physicalSubtotal > 0;
  const shipping = !hasPhysical || physicalSubtotal >= FREE ? 0 : 6.99;
  const off = discount ? Math.min(discount.amount, subtotal) : 0;
  const addresses = user?.addresses || [];

  useEffect(() => {
    if (!addresses.length) return setAddressId("");
    if (!addresses.some((a) => a.id === addressId)) setAddressId(addresses.find((a) => a.is_default)?.id || addresses[0].id);
  }, [addresses, addressId]);

  const checkout = async () => {
    if (!user) { setOpen(false); toast("Sign in to check out"); return nav("/login?next=checkout"); }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map(({ product_id, quantity, size, color, customization }) => ({ product_id, quantity, size, color, placement: customization?.placement || null })),
        origin_url: window.location.origin,
        discount_code: discount?.code || null,
        address_id: addressId || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="bg-[#0d0d10] border-white/10 w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="font-display uppercase text-2xl font-black text-white text-left">Your Cart</SheetTitle>
        </SheetHeader>
        {items.length > 0 && hasPhysical && (
          <div className="px-6 py-4 border-b border-white/10">
            <p className="font-mono text-xs text-neutral-400 mb-2" data-testid="free-shipping-msg">
              {physicalSubtotal >= FREE ? "YOU UNLOCKED FREE SHIPPING ($75+)" : `${money(FREE - physicalSubtotal)} AWAY FROM FREE SHIPPING ($75+)`}
            </p>
            <div className="h-1 bg-white/10"><div className="h-1 bg-[#FF3B30] transition-all duration-500" style={{ width: `${Math.min(100, (physicalSubtotal / FREE) * 100)}%` }} /></div>
          </div>
        )}
        {items.length > 0 && !hasPhysical && (
          <p data-testid="digital-only-msg" className="px-6 py-4 border-b border-white/10 font-mono text-xs text-emerald-400">DIGITAL ORDER · NO SHIPPING · INSTANT ACCESS</p>
        )}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {items.length === 0 && <p data-testid="cart-empty" className="text-neutral-500 text-sm">Your cart is empty. Time to gear up.</p>}
          {items.map((i) => {
            const k = keyOf(i);
            return (
              <div key={k} data-testid="cart-item" className="flex gap-4">
                <img src={i.image} alt={i.name} className="w-20 h-24 object-cover bg-neutral-900" />
                <div className="flex-1 min-w-0">
                  <p className="font-display uppercase font-bold leading-tight text-white">{i.name}</p>
                  <p data-testid="cart-item-meta" className="font-mono text-[11px] text-neutral-500 mt-1">{i.customization ? i.customization.summary : i.is_digital ? "Digital download" : [i.size, i.color].filter(Boolean).join(" · ")}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-white/15">
                      <button data-testid="cart-item-quantity-decrease" onClick={() => update(k, i.quantity - 1)} className="w-8 h-8 grid place-items-center hover:bg-white/10 hover:text-white"><Minus className="w-3 h-3" /></button>
                      <span data-testid="cart-item-quantity" className="w-8 text-center font-mono text-sm">{i.quantity}</span>
                      <button data-testid="cart-item-quantity-increase" onClick={() => update(k, Math.min(i.quantity + 1, i.stock ?? 99))} className="w-8 h-8 grid place-items-center hover:bg-white/10 hover:text-white"><Plus className="w-3 h-3" /></button>
                    </div>
                    <span className="font-mono text-sm">{money(i.price * i.quantity)}</span>
                    <button data-testid="cart-item-remove" onClick={() => remove(k)} className="text-neutral-500 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {items.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-2 bg-[#121215]">
            <DiscountBox subtotal={subtotal} />
            {user && hasPhysical && addresses.length > 0 && (
              <div className="flex items-center gap-2 bg-white/5 border border-white/15 px-3 h-10">
                <MapPin className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
                <select data-testid="checkout-address-select" value={addressId} onChange={(e) => setAddressId(e.target.value)} className="bg-transparent outline-none text-xs font-mono w-full">
                  {addresses.map((a) => <option key={a.id} value={a.id} className="bg-black">{a.label}: {a.line1}, {a.city}</option>)}
                  <option value="" className="bg-black">Enter a new address at checkout</option>
                </select>
              </div>
            )}
            <div className="flex justify-between text-sm text-neutral-400 pt-2"><span>Subtotal</span><span data-testid="cart-subtotal" className="font-mono">{money(subtotal)}</span></div>
            {off > 0 && <div className="flex justify-between text-sm text-emerald-400"><span>Discount</span><span data-testid="cart-discount" className="font-mono">-{money(off)}</span></div>}
            <div className="flex justify-between text-sm text-neutral-400"><span>Shipping</span><span data-testid="cart-shipping" className="font-mono">{shipping ? money(shipping) : hasPhysical ? "FREE" : "$0.00"}</span></div>
            <div className="flex justify-between text-white font-bold pt-2"><span className="font-display uppercase text-lg">Total</span><span data-testid="cart-total" className="font-mono">{money(subtotal - off + shipping)}</span></div>
            <button data-testid="checkout-button" onClick={checkout} disabled={loading}
              className="w-full mt-4 h-12 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider text-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Checkout
            </button>
            <p className="font-mono text-[10px] text-neutral-500 text-center pt-1">SECURE PAYMENT VIA STRIPE · TAX CALCULATED AT CHECKOUT</p>
            <p className="font-mono text-[10px] text-neutral-600 text-center">
              <Link to="/shipping-policy" onClick={() => setOpen(false)} data-testid="cart-shipping-policy-link" className="hover:text-white">SHIPPING</Link> · <Link to="/returns" onClick={() => setOpen(false)} data-testid="cart-returns-link" className="hover:text-white">RETURNS</Link> · <Link to="/terms" onClick={() => setOpen(false)} data-testid="cart-terms-link" className="hover:text-white">TERMS</Link>
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
